import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import globalUserPage from '../../../../../stores/global-user-page';
import { StudentExamRegistrationsResponse } from '../../../../../api/types/students/external-exams/external-exam-group-student';
import { StudentExamRegistration } from '../../../../../api/types/students/external-exams/student-exam-registration';
import dateUtils from '../../../../../utils/date-format';
import TabBar from '../../../../../component/tab-bar/tab-bar';
import { registerExternalExamOperation } from '../../../../external-exams/tabs/external-exams-registrations/operations/register-external-exam-operation';
import appApi from '../../../../../api/app-api';
import { toast } from 'react-toastify';
import { WarningDialog } from '../../../../../component/ui/warning-dialog/warning-dialog';
import globalUserProfile from '../../../../../stores/global-user-profile';
import { StudentProfile } from '../../../../../api/types/settings/settings-all';
import { EligibilityStatus } from '../../../../../api/types/external-exams/registration/external-exam-student-registration-eligibility';

interface ExamRowData extends StudentExamRegistration {
  groupName: string;
  groupYear: number;
}

interface UserPageStudentExternalExamsProps {
  isStudentMode?: boolean;
}

export const UserPageStudentExternalExams: React.FC<UserPageStudentExternalExamsProps> =
  observer(({ isStudentMode = false }) => {
    const [registeringExamId, setRegisteringExamId] = useState<number | null>(
      null,
    );
    const [examForRegistration, setExamForRegistration] =
      useState<ExamRowData | null>(null);

    // Handle both array and single object (type says array but server returns object)
    // Get data from appropriate source based on isStudentMode
    const externalExamsData = useMemo(() => {
      let externalExams: StudentExamRegistrationsResponse[] | undefined;

      if (isStudentMode) {
        // Student's own view - get from globalUserProfile
        const studentProfile = globalUserProfile.userProfile?.profile as
          | StudentProfile
          | undefined;
        externalExams = studentProfile?.externalExams;
      } else {
        // Admin view - get from globalUserPage
        externalExams = globalUserPage.studentDetails?.externalExams;
      }

      if (!externalExams) return null;
      // If it's an array, take first element; if it's an object, use it directly
      return Array.isArray(externalExams)
        ? externalExams[0]
        : (externalExams as any);
    }, [
      isStudentMode,
      globalUserPage.studentDetails?.externalExams,
      globalUserProfile.userProfile?.profile,
    ]);

    const reloadStudentDetails = async (userId: number) => {
      try {
        if (isStudentMode) {
          // Student's own view - reload from getHome2
          await globalUserProfile.getUserProfile();
        } else {
          // Admin view - reload from getUserStudentDetails
          const updatedUserDetails = await appApi.getUserStudentDetails(userId);
          globalUserPage.studentDetails = updatedUserDetails.data.body;
        }
      } catch (error) {
        console.error('Failed to reload student details:', error);
      }
    };

    const flattenExams = (
      groups: StudentExamRegistrationsResponse['pendingForRegistration'],
    ): ExamRowData[] => {
      const result: ExamRowData[] = [];
      groups.forEach((group) => {
        group.exams.forEach((exam) => {
          result.push({
            ...exam,
            groupName: group.group.groupName,
            groupYear: group.group.year,
          });
        });
      });
      return result;
    };

    const pendingForRegistration = useMemo(() => {
      if (!externalExamsData) return [];
      return flattenExams(externalExamsData.pendingForRegistration);
    }, [externalExamsData]);

    const upcomingRegisterExam = useMemo(() => {
      if (!externalExamsData) return [];
      return flattenExams(externalExamsData.upcomingRegisterExam);
    }, [externalExamsData]);

    const historical = useMemo(() => {
      if (!externalExamsData) return [];
      return flattenExams(externalExamsData.historical);
    }, [externalExamsData]);

    const calculatePaymentStatus = (exam: ExamRowData) => {
      if (exam.registration.paymentExempted) {
        return { text: 'פטור מתשלום', severity: 'info' as const };
      }

      const totalAmount = exam.registration.paymentService?.amount ?? 0;
      const paidAmount =
        exam.registration.userPayments?.reduce(
          (sum, payment) => sum + payment.paymentAmount,
          0,
        ) ?? 0;

      if (paidAmount === 0) {
        return { text: 'טרם שולם', severity: 'danger' as const };
      } else if (paidAmount < totalAmount) {
        return { text: 'שולם חלקית', severity: 'warning' as const };
      } else {
        return { text: 'שולם', severity: 'success' as const };
      }
    };

    const getPaidAmount = (exam: ExamRowData) => {
      return (
        exam.registration.userPayments?.reduce(
          (sum, payment) => sum + payment.paymentAmount,
          0,
        ) ?? 0
      );
    };

    const handleRegistration = async (exam: ExamRowData) => {
      const groupId = exam.exam.externalExamGroupId;
      const examId = exam.exam.id;
      const registrationId = exam.registration.id;
      const userId = exam.registration.userId;

      await registerExternalExamOperation(
        groupId,
        examId,
        registrationId,
        setRegisteringExamId,
        async () => {
          await reloadStudentDetails(userId);
        },
      );
    };

    const handlePayment = (exam: ExamRowData) => {
      toast.info(`תשלום עבור מבחן: ${exam.exam.abstractExternalExam.examName}`);
    };

    const handleCancelRegistration = (exam: ExamRowData) => {
      toast.info(
        `ביטול הרשמה למבחן: ${exam.exam.abstractExternalExam.examName}`,
      );
    };

    const renderPaymentStatus = (exam: ExamRowData) => {
      const status = calculatePaymentStatus(exam);
      return <Tag severity={status.severity} value={status.text} />;
    };

    const renderActions = (exam: ExamRowData, section: string) => {
      const isRegistered =
        exam.registration.eligibilityStatus === EligibilityStatus.REGISTER;
      const paymentStatus = calculatePaymentStatus(exam);
      const isFullyPaid =
        paymentStatus.severity === 'success' ||
        exam.registration.paymentExempted;

      return (
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          {section === 'pending' && !isRegistered && (
            <Button
              label="הרשמה"
              icon="pi pi-check"
              onClick={() => setExamForRegistration(exam)}
              loading={registeringExamId === exam.registration.id}
              disabled={registeringExamId !== null}
              style={{ height: '30px', fontSize: '14px' }}
            />
          )}
          {!exam.registration.paymentExempted &&
            !isFullyPaid &&
            (isRegistered || section === 'pending') && (
              <Button
                label="תשלום"
                icon="pi pi-credit-card"
                onClick={() => handlePayment(exam)}
                style={{ height: '30px', fontSize: '14px' }}
              />
            )}
          {isRegistered && (
            <Button
              label="ביטול הרשמה"
              icon="pi pi-times"
              severity="danger"
              onClick={() => handleCancelRegistration(exam)}
              style={{ height: '30px', fontSize: '14px' }}
            />
          )}
        </div>
      );
    };

    const renderTable = (
      exams: ExamRowData[],
      section: 'pending' | 'upcoming' | 'historical',
    ) => {
      if (exams.length === 0) {
        return (
          <div style={{ textAlign: 'right', padding: '20px', color: '#666' }}>
            לא נמצאו מבחנים
          </div>
        );
      }

      return (
        <DataTable
          value={exams}
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו מבחנים</div>
          }
          dataKey={`exam.id-${section}`}
          tableStyle={{
            direction: 'rtl',
            marginTop: '16px',
          }}
        >
          <Column
            style={{ textAlign: 'right' }}
            header="שם מקבץ"
            body={(rowData: ExamRowData) => (
              <div>
                {rowData.groupName} ({rowData.groupYear})
              </div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="שם מבחן"
            body={(rowData: ExamRowData) => (
              <div>{rowData.exam.abstractExternalExam.examName}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="תאריך מבחן"
            body={(rowData: ExamRowData) => (
              <div>
                {dateUtils.displayFormatDate(rowData.exam.startAt)}{' '}
                {new Date(rowData.exam.startAt).toLocaleTimeString('he-IL', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="פטור מתשלום"
            body={(rowData: ExamRowData) => (
              <Checkbox
                checked={rowData.registration.paymentExempted}
                disabled
              />
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="סכום לתשלום"
            body={(rowData: ExamRowData) => {
              if (rowData.registration.paymentExempted) {
                return <div>-</div>;
              }
              return (
                <div>{rowData.registration.paymentService?.amount ?? 0} ₪</div>
              );
            }}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="שולם"
            body={(rowData: ExamRowData) => {
              if (rowData.registration.paymentExempted) {
                return <div>-</div>;
              }
              return <div>{getPaidAmount(rowData)} ₪</div>;
            }}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="סטטוס תשלום"
            body={(rowData: ExamRowData) => renderPaymentStatus(rowData)}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="פעולות"
            body={(rowData: ExamRowData) => renderActions(rowData, section)}
          />
        </DataTable>
      );
    };

    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    const tabs = [
      `מבחנים הממתינים להרשמה (${pendingForRegistration.length})`,
      `מחכים לבחינה (${upcomingRegisterExam.length})`,
      `מבחנים היסטוריים (${historical.length})`,
    ];

    const tabContents = [
      renderTable(pendingForRegistration, 'pending'),
      renderTable(upcomingRegisterExam, 'upcoming'),
      renderTable(historical, 'historical'),
    ];

    return (
      <div style={{ width: '100%', padding: '20px' }}>
        <h2 style={{ marginBottom: '20px' }}>מבחנים חיצוניים</h2>

        <TabBar
          tabs={tabs}
          onClick={(index) => {
            setSelectedTabIndex(index);
          }}
        />

        <div style={{ marginTop: '20px' }}>{tabContents[selectedTabIndex]}</div>

        {examForRegistration && (
          <WarningDialog
            header="אישור הרשמה למבחן"
            isVisible={examForRegistration !== null}
            onHide={() => {
              if (registeringExamId === null) {
                setExamForRegistration(null);
              }
            }}
            addIcon={true}
            content="ההרשמה כרוכה בשירות תשלום"
            notInformation="אם אינך לחצת על כפתור זה בעבר המערכת תיצור עבורך חוב, לאחר היצצרות החוב ניתן יהיה לשלם אותו ולאחר מכן יש ללחוץ על כפתור זה שוב על מנת להשלים את ההרשמה"
            smallLetters="אם התשלום עבור מבחן זה שולם, ניתן להתעלם מהודעה זו"
            onConfirm={async () => {
              if (examForRegistration) {
                await handleRegistration(examForRegistration);
                setExamForRegistration(null);
              }
            }}
            confirmLabel="המשך להרשמה"
            confirmLoading={registeringExamId !== null}
          />
        )}
      </div>
    );
  });
